import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Container,
  Paper,
  Breadcrumbs,
  Link,
  InputBase,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TokenIcon from './TokenIcon';
import CoinChart from './CoinChart';
import { formatBalance, formatCurrency, formatTriggerPrice } from '../utils/format';
import { formatSmartNumber } from '../utils/formatNumber';
import { isRTL } from '../i18n';

// This component is a direct copy of TakeProfitStrategy, to be adapted for stop loss logic
const StopLossStrategy = ({
  coinSymbol,
  currentPrice,
  holdings,
  formData,
  setFormData,
  customValue,
  setCustomValue,
  selectedPreset,
  setSelectedPreset,
  loadingHistorical,
  historicalLows, // Use lows, not highs
  fetchHistoricalLow, // Use lows, not highs
  showTargetPriceError,
  updateStep,
  addStep,
  removeStep,
  handleSubmit,
  error,
  calculateTriggerPrices,
  loadedStrategy,
  percentValue,
  setPercentValue,
  rawPercentValue,
  setRawPercentValue
}) => {
  const { t } = useTranslation();
  const inputRef = useRef();
  const debounceRef = useRef();

  // Helper to update both percent and price
  const updateFromPercent = (percent, fromDropdown = false) => {
    setRawPercentValue(percent);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (fromDropdown) {
      // Immediate update for dropdown
      const percentStr = percent === '' ? '' : Number(percent).toFixed(1);
      setPercentValue(percentStr);
      if (currentPrice && percent !== '') {
        const price = currentPrice * (1 - Math.abs(parseFloat(percent)) / 100);
        setFormData({ ...formData, target_price: price });
        calculateTriggerPrices({ ...formData, target_price: price });
      }
      return;
    }
    // Debounced update for manual input
    debounceRef.current = setTimeout(() => {
      const percentStr = percent === '' ? '' : Number(percent).toFixed(1);
      setPercentValue(percentStr);
      if (currentPrice && percent !== '') {
        const price = currentPrice * (1 - Math.abs(parseFloat(percent)) / 100);
        setFormData({ ...formData, target_price: price });
        setSelectedPreset('custom');
        calculateTriggerPrices({ ...formData, target_price: price });
      }
    }, 4000);
  };
  const updateFromPrice = (price) => {
    setFormData({ ...formData, target_price: price });
    if (currentPrice && price !== '') {
      const percent = ((1 - parseFloat(price) / currentPrice) * 100).toFixed(1);
      setPercentValue(percent);
    }
    setSelectedPreset('custom');
    calculateTriggerPrices({ ...formData, target_price: price });
  };

  function getMatchingPreset(targetPrice, currentPrice, historicalLows) {
    if (!targetPrice || !currentPrice) return 'custom';
    const num = Number(targetPrice);
    const percent = ((1 - num / currentPrice) * 100);
    const tolerance = 0.01;
    if (Math.abs(percent - 10) < tolerance) return '-10%';
    if (Math.abs(percent - 25) < tolerance) return '-25%';
    if (Math.abs(percent - 50) < tolerance) return '-50%';
    if (Math.abs(percent - 75) < tolerance) return '-75%';
    if (historicalLows['1M'] && Math.abs(num - historicalLows['1M']) < Math.abs(num) * 0.0001) return '1M LOW';
    if (historicalLows['3M'] && Math.abs(num - historicalLows['3M']) < Math.abs(num) * 0.0001) return '3M LOW';
    if (historicalLows['6M'] && Math.abs(num - historicalLows['6M']) < Math.abs(num) * 0.0001) return '6M LOW';
    if (historicalLows['1Y'] && Math.abs(num - historicalLows['1Y']) < Math.abs(num) * 0.0001) return '1Y LOW';
    return 'custom';
  }

  // When the target price is loaded from API, set dropdown to correct preset or custom and update percent, and format value if needed
  useEffect(() => {
    // Validate and reset selectedPreset if it's invalid for stop loss strategies
    const validPresets = ['-10%', '-25%', '-50%', '-75%', '1M LOW', '3M LOW', '6M LOW', '1Y LOW', 'custom'];
    if (selectedPreset && !validPresets.includes(selectedPreset)) {
      setSelectedPreset('-25%'); // Default to -25% if current preset is invalid
    }
    
    // If this is the first load and we have a target price but no meaningful preset selection, prefer the default
    if (formData.target_price && currentPrice && (selectedPreset.includes('LOW') || selectedPreset.includes('HIGH'))) {
      const currentPercent = ((1 - parseFloat(formData.target_price) / currentPrice) * 100).toFixed(1);
      const defaultPercent = 25;
      const difference = Math.abs(parseFloat(currentPercent) - defaultPercent);
      
      // If the difference is small (≤5%), prefer the default -25% over historical presets
      if (difference <= 5) {
        setSelectedPreset('-25%');
        return;
      }
    }
    
    if (formData.target_price && currentPrice) {
      const percent = ((1 - parseFloat(formData.target_price) / currentPrice) * 100).toFixed(1);
      setPercentValue(percent);
      setRawPercentValue(percent);
      
      // Only auto-set preset if user hasn't explicitly selected 'custom' and the preset doesn't match current selection
      const preset = getMatchingPreset(formData.target_price, currentPrice, historicalLows);
      
      // Don't override if user explicitly selected 'custom' and the calculated percentage matches a preset
      if (selectedPreset === 'custom' && preset !== 'custom') {
        // User explicitly selected custom, don't override
      } else if (preset !== selectedPreset) {
        // Check if we should prefer the default -25% over historical presets
        const currentPercent = parseFloat(percent);
        const defaultPercent = 25;
        const difference = Math.abs(currentPercent - defaultPercent);
        
        // Always prefer the default -25% over historical presets unless the difference is very large (>20%)
        if ((preset.includes('LOW') || preset.includes('HIGH')) && difference <= 20) {
          setSelectedPreset('-25%');
          return;
        }
        
        setSelectedPreset(preset);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.target_price]); // Removed selectedPreset from dependencies

  // When dropdown changes
  const handleDropdownChange = (e) => {
    const val = e.target.value;
    
    let percent = '';
    let price = '';
    
    if (val === '-10%') {
      percent = 10;
    } else if (val === '-25%') {
      percent = 25;
    } else if (val === '-50%') {
      percent = 50;
    } else if (val === '-75%') {
      percent = 75;
    } else if (val === '1M LOW' && historicalLows['1M'] && currentPrice) {
      price = historicalLows['1M'];
      percent = ((1 - price / currentPrice) * 100).toFixed(1);
    } else if (val === '3M LOW' && historicalLows['3M'] && currentPrice) {
      price = historicalLows['3M'];
      percent = ((1 - price / currentPrice) * 100).toFixed(1);
    } else if (val === '6M LOW' && historicalLows['6M'] && currentPrice) {
      price = historicalLows['6M'];
      percent = ((1 - price / currentPrice) * 100).toFixed(1);
    } else if (val === '1Y LOW' && historicalLows['1Y'] && currentPrice) {
      price = historicalLows['1Y'];
      percent = ((1 - price / currentPrice) * 100).toFixed(1);
    } else if (val === 'custom') {
      // When custom is selected, always show the default -25% for stop loss
      setPercentValue('-25');
      setRawPercentValue('-25');
      setSelectedPreset('custom');
      
      // Calculate and set the target price based on the -25% default
      if (currentPrice) {
        const price = currentPrice * (1 - 25 / 100);
        setFormData({ ...formData, target_price: price });
        calculateTriggerPrices({ ...formData, target_price: price });
      }
      
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 0);
      return;
    }
    
    // For all other presets, update the selectedPreset and update the percentage
    if (percent !== '') {
      setSelectedPreset(val);
      updateFromPercent(percent, true); // fromDropdown = true
    } else if (price !== '') {
      setSelectedPreset('custom');
      setFormData({ ...formData, target_price: price });
      setPercentValue(((1 - price / currentPrice) * 100).toFixed(1));
      setRawPercentValue(((1 - price / currentPrice) * 100).toFixed(1));
      calculateTriggerPrices({ ...formData, target_price: price });
    }
  };

  // Deep compare function
  const isDirty = useMemo(() => {
    if (!loadedStrategy) return true;
    const clean = (obj) => JSON.stringify(obj, (k, v) => (v === undefined ? null : v));
    return clean(loadedStrategy) !== clean(formData);
  }, [formData, loadedStrategy]);



  return (
    <>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, mt: 1, display: 'flex', alignItems: 'baseline' }}>
        <span style={{ fontSize: '1.7em', fontWeight: 900, lineHeight: 1 }}>2<span style={{ fontSize: '1em', fontWeight: 900 }}>. </span></span>
        <span style={{ marginLeft: 6 }}>{t('exitStrategy.minTargetPrice')}</span>
      </Typography>
      <Box sx={{ mb: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          {/* Percent input - only visible when custom is selected */}
          {/* For LTR languages, show percent input on the right; for RTL, show on the left */}
          {selectedPreset === 'custom' && isRTL() && (
            <TextField
              size="small"
              type="number"
              label="Percent"
              value={rawPercentValue || ''}
              onChange={e => updateFromPercent(e.target.value, false)}
              sx={{ flex: '0 0 30%', boxSizing: 'border-box', m: 0 }}
              InputProps={{
                endAdornment: <Typography variant="caption">%</Typography>
              }}
            />
          )}
          <FormControl size="small" sx={{ flex: '0 0 70%', boxSizing: 'border-box', m: 0 }}>
            <Select
              value={(() => {
                const validPresets = ['-10%', '-25%', '-50%', '-75%', '1M LOW', '3M LOW', '6M LOW', '1Y LOW', 'custom'];
                return validPresets.includes(selectedPreset) ? selectedPreset : '-25%';
              })()}
              onChange={handleDropdownChange}
              displayEmpty
              sx={{ width: '100%', boxSizing: 'border-box', m: 0 }}
            >
              <MenuItem value={'-10%'}>-10%</MenuItem>
              <MenuItem value={'-25%'}>-25%</MenuItem>
              <MenuItem value={'-50%'}>-50%</MenuItem>
              <MenuItem value={'-75%'}>-75%</MenuItem>
              <MenuItem value={'1M LOW'} disabled={!historicalLows['1M'] || !currentPrice}>
                {historicalLows['1M'] && currentPrice ?
                  `1M LOW (${(((historicalLows['1M'] - currentPrice) / currentPrice) * 100).toFixed(1)}%)`
                  : '1M LOW (loading...)'}
              </MenuItem>
              <MenuItem value={'3M LOW'} disabled={!historicalLows['3M'] || !currentPrice}>
                {historicalLows['3M'] && currentPrice ?
                  `3M LOW (${(((historicalLows['3M'] - currentPrice) / currentPrice) * 100).toFixed(1)}%)`
                  : '3M LOW (loading...)'}
              </MenuItem>
              <MenuItem value={'6M LOW'} disabled={!historicalLows['6M'] || !currentPrice}>
                {historicalLows['6M'] && currentPrice ?
                  `6M LOW (${(((historicalLows['6M'] - currentPrice) / currentPrice) * 100).toFixed(1)}%)`
                  : '6M LOW (loading...)'}
              </MenuItem>
              <MenuItem value={'1Y LOW'} disabled={!historicalLows['1Y'] || !currentPrice}>
                {historicalLows['1Y'] && currentPrice ?
                  `1Y LOW (${(((historicalLows['1Y'] - currentPrice) / currentPrice) * 100).toFixed(1)}%)`
                  : '1Y LOW (loading...)'}
              </MenuItem>
              <MenuItem value={'custom'}>Custom</MenuItem>
            </Select>
          </FormControl>
          {/* For LTR languages, show percent input on the right */}
          {selectedPreset === 'custom' && !isRTL() && (
            <TextField
              size="small"
              type="number"
              label="Percent"
              value={rawPercentValue || ''}
              onChange={e => updateFromPercent(e.target.value, false)}
              sx={{ flex: '0 0 30%', boxSizing: 'border-box', m: 0, ml: 1 }}
              InputProps={{
                endAdornment: <Typography variant="caption">%</Typography>
              }}
            />
          )}
        </Box>
        <Box sx={{ mt: 1, width: '100%' }}>
          <TextField
            size="small"
            type="number"
            label="Target Price"
            value={formatSmartNumber(formData.target_price)}
            inputRef={inputRef}
            onChange={e => updateFromPrice(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: <Typography variant="caption">$</Typography>,
              style: { fontWeight: 700 }
            }}
            sx={{ boxSizing: 'border-box', m: 0 }}
          />
        </Box>
      </Box>
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'baseline' }}>
            <span style={{ fontSize: '1.7em', fontWeight: 900, lineHeight: 1 }}>3<span style={{ fontSize: '1em', fontWeight: 900 }}>. </span></span>
                            <span style={{ marginLeft: 6 }}>{t('exitStrategy.exitSteps')}</span>
          </Typography>
          <Button 
            variant="outlined" 
            onClick={addStep}
            disabled={formData.steps.length >= 4}
          >
            {t('exitStrategy.addStep')}
          </Button>
        </Box>

        <Box>
          {formData.steps.map((step, index) => {
            // Debug log for step and isTriggered
            // console.log('[DEBUG StopLossStrategy step]', step, 'notified_at:', step.notified_at);
            const isTriggered = step.notified_at || 
              (loadedStrategy && currentPrice <= step.trigger_price);
            // console.log(`[DEBUG StopLossStrategy isTriggered] Step ${step.step_index}:`, isTriggered);
            
            return (
              <React.Fragment key={index}>
                <Box sx={{ 
                  position: 'relative',
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 0.5, 
                  width: '100%', 
                  py: 0.5,
                  ...(isTriggered && {
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid rgba(255, 152, 0, 0.3)',
                    borderRadius: 1,
                    px: 0.5
                  })
                }}>
                  {/* First line: Step number, inputs, and calculated values */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    alignItems: 'center', 
                    gap: 1, 
                    width: '100%'
                  }}>
                    <Box sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 16, width: 18, flexShrink: 0 }}>
                      {index + 1}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <TextField
                        size="small"
                        placeholder="Trigger Price"
                        type="text"
                        value={formatTriggerPrice(step.trigger_price)}
                        onChange={e => {
                          const value = e.target.value.replace(/,/g, '');
                          if (value === '' || Number(value) > 0) {
                            updateStep(index, 'trigger_price', value);
                          }
                        }}
                        InputProps={{
                          startAdornment: <Typography variant="caption">$</Typography>
                        }}
                        error={Boolean(step.trigger_price && Number(step.trigger_price) <= 0)}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 100 }}>
                      <TextField
                        size="small"
                        placeholder="%"
                        type="number"
                        value={step.percentage_to_sell}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (Number(value) >= 0 && Number(value) <= 100)) {
                            updateStep(index, 'percentage_to_sell', value);
                          }
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 0.01
                        }}
                        InputProps={{
                          endAdornment: <Typography variant="caption">%</Typography>
                        }}
                        error={Boolean(step.percentage_to_sell && (Number(step.percentage_to_sell) <= 0 || Number(step.percentage_to_sell) > 100))}
                      />
                    </Box>
                    {holdings && step.percentage_to_sell && step.trigger_price && (
                      <Box sx={{ minWidth: 80, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          {formatBalance((holdings.balance * Number(step.percentage_to_sell) / 100))} {coinSymbol}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          {formatCurrency((holdings.balance * Number(step.percentage_to_sell) / 100) * Number(step.trigger_price))}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Second line: Status chip and delete button */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mt: 1,
                    px: 1
                  }}>
                    {isTriggered && (
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Chip 
                          label={step.notified_at ? "Triggered" : "Active"} 
                          size="small" 
                          color={step.notified_at ? "warning" : "success"} 
                        />
                      </Box>
                      )}
                    <Tooltip 
                      title={formData.steps.length === 1 && loadedStrategy 
                        ? "Delete last step and remove entire strategy" 
                        : "Delete step"
                      }
                    >
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={async () => await removeStep(index)} 
                        sx={{ ml: 'auto' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {index < formData.steps.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </React.Fragment>
            );
          })}
        </Box>

        {(() => {
          const totalPercentage = formData.steps.reduce((sum, step) => sum + Number(step.percentage_to_sell || 0), 0);
          const isOverLimit = totalPercentage > 100;
          return (
            <Typography 
              variant="body2" 
              color={isOverLimit ? "error" : "textSecondary"} 
              sx={{ mt: 2, fontWeight: isOverLimit ? 'bold' : 'normal' }}
            >
              Total: {totalPercentage.toFixed(2)}%
              {isOverLimit && (
                <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                  (Exceeds 100% limit)
                </Typography>
              )}
            </Typography>
          );
        })()}
      </Box>

      <Button 
        variant="contained" 
        onClick={handleSubmit}
        fullWidth
        size="large"
        disabled={formData.steps.reduce((sum, step) => sum + Number(step.percentage_to_sell || 0), 0) > 100 || (loadedStrategy ? !isDirty : false)}
      >
        {loadedStrategy ? t('exitStrategy.updateStrategy') : t('exitStrategy.createStrategy')}
      </Button>

      {/* Chart Section - Full Width Below */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ height: 400, mb: 2 }}>
          <CoinChart 
            symbol={coinSymbol} 
            avgBuyPrice={holdings?.avgBuyPrice} // TODO: Get from user's portfolio data
            estimatedMax={formData.target_price ? Number(formData.target_price) : null}
            exitStepLines={formData.steps
              .map((step, i) => ({ price: Number(step.trigger_price), index: i }))
              .filter(s => s.price > 0)
            }
          />
        </Box>
      </Box>
    </>
  );
};

export default StopLossStrategy; 